import type { WallDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationCondition } from "building-types/foundations/foundationEnums";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { RoomConnectionType } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomKind, RoomClass, RoomEnclosure, RoomSupport, RoomCover } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import type { PathPriority } from "./pathPriority";
import type { WallAssemblyLayerOperator, WallAssemblyOperator } from "./wallAssemblyOverrideTypes";
import type { WallSideGeneratedDatums } from "./wallSideGeneratedDatums";

/**
 * The WallDescriptor will eventually represent a full picture of the datums, assemblies and conditions on
 * either side of a wall. This is currently an incomplete picture as we cannot currently accurately
 * detect all the needed information at wall generation time. This is due to wainscoting
 * placement happening after wall generation. WallDescriptors are used for generating WallSections, which compose the differing
 * wall stata at a half edge
 *
 *
 ```
                            left side                           right side
                                                                                                               
                                            WALL DESCRIPTOR
                                                                                                               
              lDatums                                                            rDatums
                                                                 exterior
          ceiling top --------------- +---------++--+
                                      | ceiling ||  |+---------+ --------------- ceiling top
       ceiling bottom --------------- +---------+|  || ceiling |
                                                 |  |+---------+ --------------- ceiling bottom
                                                 |  |
                                                 |  |
                              interior           |  |            interior
                                                 |  |
                                                 |  |
                                                 |  |+---------+ --------------- floor top
                                                 |  ||  floor  |
                                                 |  |+---------+ --------------- floor bottom
                                                 +--+
 floor/foundation top --------------- +-------------+ -------------------------- foundation top
                                      |    slab     |            exterior
         floor bottom --------------- +-------------+
                                               |     |
                                               |     |
    foundation bottom ------------------------ +-----+ -------------------------foundation botom
                                                                                                               
                                          floorPriority : LEFT
                                                                                                               
                                                                                                               
                                                                                                               
                                                                                                               
                                             WALL SECTIONS
                                                                                                               
                      lCladding       lStructure       rStructure     rCladding
                                                                                                               
                                                                                                               
          ceiling top -------------------------------- WALL           EXTERIOR
                      NONE            CEILING   -------------------------------- ceiling top
       ceiling bottom -------------------------------- CEILING        NONE
                                                -------------------------------- ceiling bottom
                                                 |  |
                                                 |  |
                      INTERIOR        WALL       |  |  WALL           INTERIOR
                                                 |  |
                                                 |  |
                                                -------------------------------- floor top
                                                 |  |  FLOOR          NONE
                                                -------------------------------- floor bottom
                                                 |  |  WALL           EXTERIOR
 floor/foundation top ---------------------------------------------------------- foundation top
                      NONE            FNDTN      |  |  FNDTN          NONE
         floor bottom --------------------------------
  
  
                                           OUT_OF_BOUNDS
```
 *
 */
export interface WallDescriptor {
  readonly leftSide: WallSideDescriptor;
  readonly rightSide: WallSideDescriptor;
  readonly floorPriority: PathPriority;
  // Foundation Information
  readonly isCripple?: boolean;
  readonly isFurring?: boolean;
  readonly foundationCondition: FoundationCondition;
  readonly foundationAssembly?: FoundationAssembly;
  // Roof Information
  readonly isHalfStory?: boolean;
  readonly wallOperators: WallOperators;
}

/**
 * @NOTE - this interface is very deliberately not integrated into the WallSideDescriptor
 * because it is in flux, and will eventually be replaced with a WallOperatorTable or something similar
 */
export interface WallOperators {
  // LEFT SIDE
  // ------------------------------------------------------------
  readonly leftFinishOperators: WallAssemblyOperator[];

  // CORE BOUNDARY
  // Operators in this section need edge fighing heuristics
  // ------------------------------------------------------------
  readonly leftCoreOperators: WallAssemblyOperator[];
  readonly rightCoreOperators: WallAssemblyOperator[];
  // ------------------------------------------------------------
  // CORE BOUNDARY

  readonly rightFinishOperators: WallAssemblyOperator[];
  // ------------------------------------------------------------
  // RIGHT SIDE
}

export interface WallSideRoomDescriptor {
  readonly roomLevelId: string;
  readonly room?: Room;
  readonly roomEdgeIndex?: number;
  readonly roomKind: RoomKind;
  readonly roomClass: RoomClass;
  readonly roomEnclosure: RoomEnclosure;
  readonly roomSupport: RoomSupport;
  readonly roomCover: RoomCover;
  readonly roomConnection?: RoomConnectionType;
  readonly roomHalfStory?: boolean; // TODO - maybe need an addition type here
  readonly roomWallOperators: WallAssemblyLayerOperator[];
}

export interface WallSideDescriptor {
  readonly wallSideDatums: WallSideGeneratedDatums;

  /**
   * Facades attached to this WallSide via various sources (right now only half edges).
   * These facades may not appear on the final wall, since the wall sections generated
   * on that wall will determine their application.
   *
   * The order of these is important - their application order will affect the
   * final render order and layer thicknesses.
   *
   * Once there are more application methods for facades, this might look something like
   * wallFacades = [
   *   ...facadesFromSettings
   *   ...facadesFromSelectors
   *   ...facadesFromOppositeSide
   *   ...facadesFromWallSide
   * ]
   *
   * If facades have operators like ADD/REMOVE/INSERT, they could then be applied using their
   * operators to the wall assembly as it is being generated similar to a reducer
   */
  readonly wallFacadeSpans: FacadeSpanStratum[]; // deprecated?
  readonly wallFacadeSweeps: FacadeSweepStratum[];
  readonly wallFacadeDatums: Table<WallDatumAbstraction>;

  /**
   * Information about the room on this side of the wall.
   * @NOTE that there may not be a room on this WallSide, but all other properties still hold
   */
  readonly wallRoom: WallSideRoomDescriptor;

  /**
   * Material Keys for wall cladding sections
   * */
  readonly wallExteriorMaterial: ExteriorMaterial;

  /**
   * @TODO - floorStructuralType and floorAssemblyType should be
   * replaced with floorAssembly of @type {FloorAssembly} once slabs are floorAssemblies
   */
  readonly floorStructuralType: StructuralFloorType;
  readonly floorAssemblyType: FloorAssemblyType;
}

export enum WallSideCutType {
  FOUNDATION,
  LOWER,
  FLOOR,
  ROOM,
  CEILING,
  UPPER,
  ROOF,
  OUT_OF_BOUNDS,
}

export enum StructuralAssemblyType {
  NONE = "NONE",
  WALL = "WALL",
  FLOOR = "FLOOR",
  CEILING = "CEILING",
  FOUNDATION = "FOUNDATION",
  ROOF = "ROOF",
}

export enum WallCladdingType {
  NONE = "NONE",
  INTERIOR = "INTERIOR",
  EXTERIOR = "EXTERIOR",
}

interface WallSectionDomain {
  readonly startOffset: number; // TODO - need a nominal number
  readonly endOffset: number; // TODO - need a nominal number
  readonly topOffset: LevelInches;
  readonly bottomOffset: LevelInches;
}

export interface WallSectionSide {
  readonly structure: StructuralAssemblyType;
  readonly cladding: WallCladdingType;
}

export interface WallSection extends WallSectionDomain {
  readonly leftSide: WallSectionSide;
  readonly rightSide: WallSectionSide;
}
